import { Tooltip } from 'antd';
import React from 'react';

export interface TeamMemberPillProps {
  avatar?: string;
  initials?: string;
  role?: string;
  name?: string;
  count?: number;
  size?: 'small' | 'default';
  isActive?: number; // 1 = active, 0 = inactive
  lastWorkedDate?: string | null;
}

const getRoleLabel = (role?: string): string => role?.replaceAll('_', ' ').trim() ?? '';

export function TeamMemberPill({
  avatar,
  initials,
  role,
  name,
  count,
  size = 'default',
  isActive,
  lastWorkedDate,
}: Readonly<TeamMemberPillProps>): React.ReactElement {
  const isSmall = size === 'small';
  const sizeClass = isSmall ? '--small' : '--default';
  const roleLabel = getRoleLabel(role);
  const isInactive = isActive === 0;

  let tooltipTitle =
    roleLabel && name ? `${roleLabel} ${name}` : name || roleLabel || 'Team Member';

  if (isInactive) {
    tooltipTitle = lastWorkedDate
      ? `${tooltipTitle} · No longer active since ${lastWorkedDate}`
      : `${tooltipTitle} · No longer active`;
  }

  return (
    <Tooltip title={tooltipTitle}>
      <div
        className={`team-member-pill team-member-pill${sizeClass}`}
        style={isInactive ? { opacity: 0.6 } : undefined}
        aria-label={tooltipTitle}
      >
        {avatar || initials ? (
          <div className={`team-member-pill__avatar team-member-pill__avatar${sizeClass}`}>
            {avatar || initials}
          </div>
        ) : null}
        {(roleLabel || name) && (
          <div className="team-member-pill__content">
            {roleLabel && <div className="team-member-pill__role">{roleLabel}</div>}
            {name && (
              <span className={`team-member-pill__text team-member-pill__text${sizeClass}`}>
                {name}
              </span>
            )}
          </div>
        )}
        {count !== undefined && count > 0 ? (
          <span className={`team-member-pill__count team-member-pill__count${sizeClass}`}>
            +{count}
          </span>
        ) : null}
      </div>
    </Tooltip>
  );
}
